import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { reportsService } from '@/services/reports';
import { ReportAccessDenied } from '@/pages/reports/components/ReportAccessDenied';
import { useAuth } from '@/contexts/auth';
import { isAppError } from '@/lib/errors';
import { Report } from '@/pages/reports/schemas';
import { usePostHogReportProperties } from '@/hooks/use-posthog-report-properties';
import { usePostHogReportClosed } from '@/hooks/use-posthog-report-closed';
import { usePostHogReportScrolled } from '@/hooks/use-posthog-report-scrolled';
import { usePostHogReportAttention } from '@/hooks/use-posthog-report-attention';
import { trackReportLoadError } from '@/lib/reportErrors';
import { markReportPerformanceStart } from '@/lib/reportPerformance';
import {
  LoadingState,
  ErrorState,
  ReportContent,
} from './components';

const VIEW_DURATION_PING_INTERVAL_MS = 10_000;

const getViewIdentifier = (
  report: { user_id?: string; partner_email_id?: string } | null | undefined
): { user_id: string } | { partner_email_id: string } | null => {
  if (!report) return null;
  if (report.user_id) return { user_id: report.user_id };
  if (report.partner_email_id) return { partner_email_id: report.partner_email_id };
  return null;
};

export const ReportPreviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, logout } = useAuth();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadCompleted, setLoadCompleted] = useState(false);
  const [authRedirecting, setAuthRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [viewId, setViewId] = useState<string | null>(null);
  const reportIdRef = useRef<string | null>(null);
  const viewPingedRef = useRef(false);

  const emailFromQuery = searchParams.get('email') ?? undefined;

  const { isBrandReady } = usePostHogReportProperties({
    enabled: Boolean(id),
    reportId: report?.id ?? id,
    reportDate: report?.createdAt?.toISOString(),
    reportBrand: report?.partner_name ?? null,
    reportPartnerId: report?.partner_id ?? null,
    reportLang: report?.locale ?? report?.lang ?? null,
    captureOpenEvents: Boolean(report),
  });

  usePostHogReportClosed({
    enabled: !!report && isBrandReady,
    reportId: report?.id,
  });

  usePostHogReportScrolled({
    enabled: !!report && isBrandReady,
    reportId: report?.id,
  });

  usePostHogReportAttention({
    enabled: !!report && isBrandReady,
    reportId: report?.id,
  });

  const loadReport = useCallback(async () => {
    if (!id) return;
    markReportPerformanceStart(id);
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    setLoadCompleted(false);
    try {
      const email = emailFromQuery || user?.email;
      const reportData = await reportsService.getReportById(id, email);
      if (!reportData) {
        setError('Report não encontrado');
        setReport(null);
        trackReportLoadError(id, 'Report não encontrado', 'network');
      } else if (!reportData.content || reportData.content.length === 0) {
        setError('Report sem conteúdo');
        setReport(null);
        trackReportLoadError(id, 'Report content is empty', 'render');
      } else {
        setReport(reportData);
        setError(null);
      }
    } catch (err) {
      if (isAppError(err) && err.status === 401) {
        setAuthRedirecting(true);
        logout();
        navigate(
          `/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
          { replace: true }
        );
        return;
      }
      if (isAppError(err) && err.status === 403) {
        setAccessDenied(true);
        setReport(null);
        return;
      }
      setError('Erro ao carregar report');
      setReport(null);
      trackReportLoadError(id, err);
      console.error('Erro ao carregar report:', err);
    } finally {
      setLoading(false);
      setLoadCompleted(true);
    }
  }, [id, emailFromQuery, user?.email, logout, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (!id) {
      setError('ID do report não fornecido');
      setLoading(false);
      setLoadCompleted(true);
      return;
    }
    loadReport();
  }, [id, loadReport]);

  const viewIdentifier = getViewIdentifier(report);
  const canTrackView = !!viewIdentifier;
  useEffect(() => {
    if (!id || !report || !canTrackView || !viewIdentifier) return;
    if (reportIdRef.current !== id) {
      viewPingedRef.current = false;
      setViewId(null);
    }
    if (viewPingedRef.current) return;
    viewPingedRef.current = true;
    reportIdRef.current = id;
    reportsService
      .addView(id, viewIdentifier)
      .then((data) => {
        const raw = data as { id?: string; view_id?: string; view?: { id?: string } } | undefined;
        const vid = raw?.id ?? raw?.view_id ?? raw?.view?.id;
        if (vid) setViewId(vid);
      })
      .catch(() => {});
  }, [id, report, canTrackView, viewIdentifier]);

  useEffect(() => {
    if (!id || !viewId) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    let accumulatedMs = 0;
    let visibleSince: number | null = document.hidden ? null : Date.now();

    const getDurationMs = () => {
      const activeMs = visibleSince != null ? Date.now() - visibleSince : 0;
      return accumulatedMs + activeMs;
    };

    const sendPing = () => {
      const reportId = reportIdRef.current;
      if (!reportId) return;
      reportsService.updateViewDuration(reportId, viewId, getDurationMs()).catch(() => {});
    };

    const clearPingInterval = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const pauseTracking = () => {
      clearPingInterval();
      if (visibleSince != null) {
        accumulatedMs += Date.now() - visibleSince;
        visibleSince = null;
      }
    };

    const startPingInterval = () => {
      clearPingInterval();
      intervalId = setInterval(sendPing, VIEW_DURATION_PING_INTERVAL_MS);
    };

    const resumeTracking = () => {
      if (document.hidden) return;
      visibleSince = Date.now();
      startPingInterval();
      sendPing();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseTracking();
        sendPing();
        return;
      }
      resumeTracking();
    };

    const handlePageHide = () => {
      pauseTracking();
      sendPing();
    };

    if (!document.hidden) {
      resumeTracking();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      pauseTracking();
    };
  }, [id, viewId]);

  if (loading || !loadCompleted || authRedirecting || authLoading) {
    return <LoadingState message="Carregando Report..." />;
  }

  if (accessDenied) {
    return <ReportAccessDenied />;
  }

  if (error || !report) {
    return (
      <ErrorState
        title="Este report não existe."
        description="Verifique o link ou volte para a página inicial."
        onBack={() => navigate('/')}
        backButtonLabel="Ir para a página inicial"
        reportNotFound
      />
    );
  }

  if (report.content.length === 0) {
    return (
      <ErrorState
        title="Este report não existe."
        description="Verifique o link ou volte para a página inicial."
        onBack={() => navigate('/')}
        backButtonLabel="Ir para a página inicial"
        reportNotFound
      />
    );
  }

  return (
    <div className="min-h-screen bg-white w-full">
      <ReportContent report={report} analyticsEnabled={isBrandReady} />
    </div>
  );
};
